import { queryOptions, useQuery } from "@tanstack/react-query";

import { blastHolesAjax } from "../../ajax/BlastHolesAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { DrillingEntryMode, DrillingSessionHolesDTO, HoleDipDTO, PatternChargingDTO, PatternHolesDTO } from "../../DTOs/DrillBlast/BlastHoleDTO";
import { queryClient } from "../../queryClient";
import { drillBlastQuery } from "./useDrillBlastLookups";

export const patternHolesQuery = (patternBlastId: number) => drillBlastQuery<PatternHolesDTO>(['drillBlast', 'patternHoles', patternBlastId], 'the holes',
  () => blastHolesAjax.getHoles(patternBlastId));

export const usePatternHoles = (patternBlastId: number) => useQuery(patternHolesQuery(patternBlastId));

// Note: a pattern added offline is not on the server yet, so its holes come from the pattern's saved plan with nothing
// drilled, and the result never goes stale so the attempts queued against it stay on screen until it syncs.
export const drillingSessionHolesQuery = (drillingBlastId: number, patternBlastId: number) => queryOptions({
  queryKey: ['drillBlast', 'drillingSessionHoles', drillingBlastId],
  ...(drillingBlastId < 0 ? { staleTime: Infinity } : {}),
  queryFn: async (): Promise<DrillingSessionHolesDTO> => {
    if (drillingBlastId < 0) {
      const holes = queryClient.getQueryData(patternHolesQuery(patternBlastId).queryKey)
        ?? await queryClient.fetchQuery(patternHolesQuery(patternBlastId));
      return { drillingBlastId, patternBlastId, reference: holes.reference, entryMode: DrillingEntryMode.Hole, canChangeMode: false, attempts: [], holes };
    }
    const res = await blastHolesAjax.getSession(drillingBlastId);
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the holes drilled');
    return res;
  },
});

export const useDrillingSessionHoles = (drillingBlastId: number, patternBlastId: number, enabled = true) =>
  useQuery({ ...drillingSessionHolesQuery(drillingBlastId, patternBlastId), enabled });

export const patternDipsQuery = (patternBlastId: number) => drillBlastQuery<HoleDipDTO[]>(['drillBlast', 'patternDips', patternBlastId], 'the dips',
  () => blastHolesAjax.getDips(patternBlastId));

export const usePatternDips = (patternBlastId: number) => useQuery(patternDipsQuery(patternBlastId));

export const patternChargingQuery = (patternBlastId: number) => drillBlastQuery<PatternChargingDTO>(['drillBlast', 'patternCharging', patternBlastId],
  'the charging', () => blastHolesAjax.getCharging(patternBlastId));

export const usePatternCharging = (patternBlastId: number) => useQuery(patternChargingQuery(patternBlastId));
